import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { toast } from 'react-toastify';
import dig from 'object-dig';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import StarIcon from '@material-ui/icons/Star';
import SaveAlt from '@material-ui/icons/SaveAlt';

import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';
import LinkMaterial from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import GridContainer from '~/components/GridContainer';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import InputSearch from '~/components/Input/InputSearch';
import InputSelectAsync from '~/components/Input/InputSelectAsync';
import InputSelect from '~/components/Input/InputSelect';
import InputSwitch from '~/components/Input/InputSwitch';

import Request from '~/providers/Request';

import toInt from '~/utils/Number/toInt';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';
import isBlank from '~/utils/isBlank';
import CurrentUser from '~/contexts/CurrentUser';
import fetchCorporationPartnerRelationship from '~/utils/fetchCorporationPartnerRelationship';

import { showPerson } from '~/config/routes';
import { URL } from '~/config/uri';
import InputMultiCorporationWithPrivate from '~/components/Input/InputMultiCorporationWithPrivate';

function ActiveClients() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const [loading, setLoading] = useState(false);
  const [userActives, setUserActives] = useState([]);
  const [totalUserActives, setTotalUserActives] = useState(0);
  const [page, setPage] = useState(0);
  const [corporation, setCorporation] = useState([
    {
      corporation_id: null,
      id: null,
      name: 'Todas',
    },
  ]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [inputSearch, setInputSearch] = useState('');
  const { user } = useContext(CurrentUser);
  const [planType, setPlanType] = useState({ id: null, name: t('all') });
  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [specialty, setSpecialty] = useState({ id: 0, name: t('all') });
  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);
  const [allActiveClients, setAllActiveClients] = useState(0);
  const [isExpiredClients, setIsExpiredClients] = useState(false);
  const [situationFilter, setSituationFilter] = useState('active');

  const situationOptions = [
    {
      id: 'active',
      name: 'Ativos',
    },
    {
      id: 'inactive',
      name: 'Excluídos',
    },
    {
      id: 'all',
      name: 'Todos',
    },
  ];

  const trAttr = (attr) => t(`activeClients.attributes.${attr}`);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function generateFile(filters, type) {
    try {
      const registers = await Request.get(
        '/admin/report/active_users',
        filters,
        {
          normalizeJSON: false,
        }
      );

      const rows = [];

      rows.push(['Relatório de usuários ativos']);
      rows.push([]);
      rows.push([
        'Período',
        `${moment(filters.start).format('DD/MM/YYYY HH:mm')} a ${moment(
          filters.end
        ).format('DD/MM/YYYY HH:mm')}`,
      ]);
      rows.push(['Total de usuários ativos', `${allActiveClients} usuários`]);

      rows.push(['']);
      rows.push([
        'ID',
        'Nome do usuário',
        'CPF',
        'Titular',
        'Empresas',
        'Plano',
        'Especialidade',
        'Data de vínculo',
        'Data Vencimento Plano',
        'Data de exclusão',
      ]);
      registers.data.map((item) => {
        const isGeneralPractitioner =
          item.pack_type === 'doctor' ? 'Clínico Geral' : '';
        const doctorPlan =
          item.pack_type === 'doctor_all'
            ? 'Médico especialista'
            : 'Televida médico';
        const expiresDate =
          item.package_expires_at || item.policy_expires_at
            ? moment(item.package_expires_at || item.policy_expires_at).format(
                'DD/MM/YYYY'
              )
            : 'Sem vencimento';
        const deletedAt = item.deleted_at
          ? moment(item.deleted_at).format('DD/MM/YYYY')
          : 'Sem exclusão';

        if (isBlank(item.deleted_at)) {
          if (item.partner_name === null) item.partner_name = 'Particular';
          rows.push([
            item.id.toString(),
            item.name,
            item.cpf,
            item.owner_name,
            item.corporation_name,
            item.plan ?? doctorPlan,
            item.specialty ?? isGeneralPractitioner,
            moment(item.created_at).format('DD/MM/YYYY'),
            expiresDate,
            deletedAt,
            item.user_corporation_name,
          ]);
        }

        return item;
      });

      rows.push(['']);
      rows.push(['Relatório gerado por', user.name]);
      rows.push([
        'Relatório gerado em',
        `${moment().format('L')} - ${moment().format('LT')}`,
      ]);

      if (type === 'csv')
        csvGenerator('relatorio_usuarios_ativos.csv', [...rows]);
      else xlsxGenerator('relatorio_usuarios_ativos.xlsx', [...rows]);

      toast.success(
        `Arquivo ${type.toLocaleUpperCase()} de usuários ativos gerado com sucesso!`
      );

      const totalPages = toInt(registers.meta.total) / registers.meta.perPage;
      if (registers.meta.page < totalPages) {
        filters.page += 1;
        generateFile(filters, type);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()} de usuários ativos`
      );
    }
  }

  async function fetchPlanType() {
    try {
      const response = await fetch(`${URL}/admin/products/list`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${await Request.getAuth()}` },
      });
      const data = await response.json();

      setPlanTypeOptions([{ id: null, name: t('all') }, ...data]);
    } catch (error) {
      console.log(error);
      setPlanTypeOptions([{ id: null, name: t('all') }]);
    }
  }

  async function fetchSpecialties() {
    if (corporation !== null) {
      const dataPromises = corporation.map(async (corp) => {
        const data = await Request.get(
          '/admin/specialties',
          {
            corporation_id: corp.corporation_id,
          },
          { normalizeJSON: true }
        );
        return data.data;
      });

      Promise.all(dataPromises)
        .then((data) => {
          const allData = data.flat();

          const arr = [{ id: 0, name: t('all') }, ...allData];
          const uniqueArr = [
            ...new Map(arr.map((item) => [item.id, item])).values(),
          ];

          setSpecialtiesOptions(uniqueArr);
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  }

  const filteredCorporation = corporation.filter(
    (corp) => corp.id !== null || corp.id !== 0
  );

  const corpsId = filteredCorporation
    ? `[${filteredCorporation.map((corp) => corp.id).join(',')}]`
    : null;

  const corpsCorporationId = corporation
    ? `[${filteredCorporation.map((corp) => corp.corporation_id).join(',')}]`
    : null;

  function onExport(type) {
    const filters = {
      qs: inputSearch,
      page: 1,
      per_page: 50000,
      corporate_partner_id: dig(corporation, 'id'),
      product_id: dig(planType, 'id'),
      specialty_id: dig(specialty, 'id'),
      expired: isExpiredClients,
      situation: situationFilter,
      partners: corpsId,
      corporations: corpsCorporationId,
    };
    toast.info(`Arquivo ${type.toLocaleUpperCase()} esta sendo gerado!`);
    generateFile(filters, type);
  }

  async function getActiveUsers() {
    setLoading(true);

    const filters = {
      page: page + 1,
      qs: inputSearch,
      per_page: rowsPerPage,
      corporate_partner_id: dig(corporation, 'id'),
      product_id: dig(planType, 'id'),
      specialty_id: dig(specialty, 'id'),
      expired: isExpiredClients,
      situation: situationFilter,
      partners: corpsId,
      corporations: corpsCorporationId,
    };

    Request.get('/admin/report/active_users', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setUserActives(data);
        setPage(toInt(meta.page) - 1);
        setTotalUserActives(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });

    try {
      const response = await fetch(
        `${URL}/admin/report/active_users/total?qs=${inputSearch}&corporate_partner_id=${
          dig(corporation, 'id') ?? ''
        }&product_id=${dig(planType, 'id') ?? ''}&specialty_id=${
          dig(specialty, 'id') ?? ''
        }`,
        {
          headers: { Authorization: `Bearer ${await Request.getAuth()}` },
        }
      );

      const data = await response.json();
      setAllActiveClients(data.total);
    } catch (error) {
      console.log(error);
      setAllActiveClients(0);
    }
  }

  useEffect(() => {
    getActiveUsers();
  }, [
    page,
    inputSearch,
    rowsPerPage,
    planType,
    corporation,
    specialty,
    isExpiredClients,
    situationFilter,
  ]);

  useEffect(() => {
    fetchPlanType();
  }, []);

  useEffect(() => {
    fetchSpecialties(corporation.id);
  }, [corporation]);

  return (
    <>
      <Paper
        Icon={StarIcon}
        iconColor="blue"
        title={t('dashboard.attributes.active_clients')}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputMultiCorporationWithPrivate
              corporation={corporation}
              setCorporation={setCorporation}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              name={'planType'}
              value={planType}
              label={t('activeClients.extras.planType')}
              inputComponent={InputSelect}
              onChange={({ planType }) => {
                setSpecialty(null);
                setPlanType(planType);
              }}
              InputProps={{
                isClearable: false,
                outValueType: 'object',
                options: planTypeOptions,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              name={'specialty'}
              value={specialty}
              label={t('activeClients.extras.specialty')}
              inputComponent={InputSelect}
              onChange={({ specialty }) => {
                setPlanType(null);
                setSpecialty(specialty);
              }}
              InputProps={{
                isClearable: false,
                options: specialtiesOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              name={'situationFilter'}
              value={situationFilter}
              label={t('activeClients.extras.situationFilter')}
              inputComponent={InputSelect}
              onChange={({ situationFilter }) => {
                setSituationFilter(situationFilter.id);
              }}
              InputProps={{
                isClearable: false,
                options: situationOptions,
                utValueType: 'string',
              }}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={6} md={4} lg={4} xl={4}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('activeClients.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputSwitch
              label={t('activeClients.extras.justExpiredPlans')}
              value={isExpiredClients}
              onChange={setIsExpiredClients}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem align="right">
            <Button
              color="second"
              onClick={() => {
                onExport('csv');
              }}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                onExport('xlsx');
              }}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={userActives.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {trAttr('id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('username')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('document')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('holder')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('company')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('plan')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('specialty')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('bindingDate')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('planExpiredDate')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('dateExclusion')}
              </TableCell>
            </>
          }
        >
          {userActives.map((row, key) => {
            const doctorPlan =
              row.pack_type === 'doctor_all'
                ? 'Médico especialista'
                : 'Televida médico';

            const isGeneralPractitioner =
              row.pack_type === 'doctor' ? 'Clínico Geral' : '';

            const expiresDate =
              row.package_expires_at || row.policy_expires_at
                ? moment(
                    row.package_expires_at || row.policy_expires_at
                  ).format('DD/MM/YYYY')
                : 'Sem vencimento';

            const deletedAt = row.deleted_at
              ? moment(row.deleted_at).format('DD/MM/YYYY')
              : 'Sem exclusão';

            return (
              <TableRow key={key} hover className={classesTable.row}>
                <TableCell>
                  <Chip
                    label={row.id}
                    component={LinkMaterial}
                    href={showPerson.reverse({
                      id: row.id,
                    })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.cpf}</TableCell>
                <TableCell>{row.owner_name}</TableCell>
                <TableCell>{row.corporation_name}</TableCell>
                <TableCell>{row.plan ?? doctorPlan}</TableCell>
                <TableCell>{row.specialty ?? isGeneralPractitioner}</TableCell>
                <TableCell>
                  {moment(row.created_at).format('DD/MM/YYYY')}
                </TableCell>
                <TableCell>{expiresDate}</TableCell>
                <TableCell>{deletedAt}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 2 }}
        >
          <strong>Total de usuários ativos: </strong>
          {allActiveClients}
        </Typography>

        <TablePagination
          count={totalUserActives}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default ActiveClients;
